import { useState } from "react";
import { useApi, apiPatch } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SeverityChip } from "@/components/common/SeverityChip";
import { LoadingBlock, ErrorState, EmptyState } from "@/components/common/StateViews";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { timeAgo, VENDOR_LABEL, fmtNum } from "@/lib/format";
import { ShieldAlert, Radar, Eye } from "lucide-react";

const COLUMNS = ["New", "Investigating", "Contained", "Resolved"];

export default function Risk() {
  const { data: incidents = [], isLoading, error, refetch } = useApi("incidents", "/risk/incidents");
  const { data: shadow = [] } = useApi("shadow", "/risk/shadow-apps");
  const [selected, setSelected] = useState(null);

  const move = async (inc, status) => {
    await apiPatch(`/risk/incidents/${inc.id}`, { status });
    toast.success(`Moved to ${status}`); refetch(); setSelected(null);
  };
  const pushSiem = (inc) => toast.success(`Incident ${inc.id} pushed to Sentinel`);

  if (error) return <ErrorState />;

  return (
    <div>
      <PageHeader eyebrow="Navitra Guard · Risk" title="Risk & Incidents" subtitle="Triage live AI risk events, review shadow-AI, and push incidents to your SIEM." />
      <Tabs defaultValue="board">
        <TabsList><TabsTrigger value="board" data-testid="tab-board"><ShieldAlert className="h-4 w-4 mr-1.5" />Triage board</TabsTrigger><TabsTrigger value="shadow" data-testid="tab-shadow"><Radar className="h-4 w-4 mr-1.5" />Shadow AI</TabsTrigger></TabsList>

        <TabsContent value="board" className="mt-4">
          {isLoading ? <LoadingBlock /> : (
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
              {COLUMNS.map((col) => {
                const items = incidents.filter((i) => i.status === col);
                return (
                  <div key={col} className="rounded-xl border bg-card/60 p-3">
                    <div className="flex items-center justify-between mb-3 px-1"><span className="text-sm font-semibold">{col}</span><span className="text-xs mono text-muted-foreground">{items.length}</span></div>
                    <div className="space-y-2">
                      {items.length === 0 ? <p className="text-xs text-muted-foreground px-1 py-4">No incidents</p> : items.map((i) => (
                        <button key={i.id} onClick={() => setSelected(i)} data-testid={`incident-${i.id}`} className="w-full text-left rounded-lg border bg-card p-3 hover:border-gold/50 transition-colors">
                          <div className="flex items-start justify-between gap-2"><SeverityChip level={i.severity} dot /><span className="text-[11px] text-muted-foreground">{timeAgo(i.created)}</span></div>
                          <p className="text-sm font-medium mt-2 leading-snug">{i.title}</p>
                          <p className="text-xs text-muted-foreground mt-1">{VENDOR_LABEL[i.vendor]} · {i.assignee}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="shadow" className="mt-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shadow.map((a) => (
              <Card key={a.id}>
                <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-base">{a.name}</CardTitle><SeverityChip level={a.status} /></div></CardHeader>
                <CardContent className="text-sm space-y-2">
                  <div className="flex justify-between"><span className="text-muted-foreground">Risk score</span><span className="mono font-semibold">{a.risk_score}/100</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Users (30d)</span><span className="mono">{fmtNum(a.users_30d)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Data volume</span><span className="mono">{fmtNum(a.data_volume_mb)} MB</span></div>
                  <p className="text-xs text-muted-foreground pt-1">{a.notes}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          {selected && (<>
            <SheetHeader><SheetTitle className="font-display flex items-center gap-2"><SeverityChip level={selected.severity} dot />{selected.title}</SheetTitle><SheetDescription>{VENDOR_LABEL[selected.vendor]} · opened {timeAgo(selected.created)} · {selected.user}</SheetDescription></SheetHeader>
            <div className="mt-5 space-y-4">
              <div className="rounded-lg border p-4"><p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Recommended action</p><p className="text-sm">{selected.recommended_action}</p></div>
              <div><p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Move to</p>
                <div className="flex flex-wrap gap-2">{COLUMNS.map((c) => <Button key={c} size="sm" variant={c === selected.status ? "default" : "outline"} onClick={() => move(selected, c)} data-testid={`move-${c}`}>{c}</Button>)}</div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => pushSiem(selected)} data-testid="push-siem">Push to SIEM</Button>
                <Button variant="outline" size="sm" onClick={() => toast.success("Content review requested (JIT)")}><Eye className="h-4 w-4 mr-1.5" />JIT content view</Button>
              </div>
            </div>
          </>)}
        </SheetContent>
      </Sheet>
    </div>
  );
}
